import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import {
  useCreateReportSchedule,
  useUpdateReportSchedule,
} from "@/hooks/use-api";
import type { ReportSchedule } from "@/types/api";

interface ScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: ReportSchedule | null;
  isMsp?: boolean;
}

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export function ScheduleDialog({
  open,
  onOpenChange,
  schedule,
  isMsp,
}: ScheduleDialogProps) {
  const isEditing = !!schedule;

  const [reportType, setReportType] = useState<string>("posture");
  const [frequency, setFrequency] = useState<string>("monthly");
  const [dayOfWeek, setDayOfWeek] = useState<number>(0);
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [recipientsText, setRecipientsText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateReportSchedule();
  const updateMutation = useUpdateReportSchedule();

  useEffect(() => {
    if (schedule) {
      setReportType(schedule.report_type);
      setFrequency(schedule.frequency);
      setDayOfWeek(schedule.day_of_week ?? 0);
      setDayOfMonth(schedule.day_of_month ?? 1);
      setRecipientsText(schedule.recipients.join(", "));
    } else {
      setReportType("posture");
      setFrequency("monthly");
      setDayOfWeek(0);
      setDayOfMonth(1);
      setRecipientsText("");
    }
    setError(null);
  }, [schedule, open]);

  function handleSubmit() {
    setError(null);

    const recipients = recipientsText
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    if (recipients.length === 0) {
      setError("At least one recipient email is required");
      return;
    }

    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    for (const email of recipients) {
      if (!emailRe.test(email)) {
        setError(`Invalid email address: ${email}`);
        return;
      }
    }

    if (recipients.length > 10) {
      setError("Maximum 10 recipients allowed");
      return;
    }

    if (isEditing && schedule) {
      updateMutation.mutate(
        {
          scheduleId: schedule.id,
          frequency: frequency as "weekly" | "monthly",
          recipients,
          day_of_week: frequency === "weekly" ? dayOfWeek : undefined,
          day_of_month: frequency === "monthly" ? dayOfMonth : undefined,
        },
        {
          onSuccess: () => onOpenChange(false),
          onError: (err) => setError(err.message || "Failed to update schedule"),
        },
      );
    } else {
      createMutation.mutate(
        {
          report_type: reportType as "posture" | "msp_portfolio",
          frequency: frequency as "weekly" | "monthly",
          recipients,
          day_of_week: frequency === "weekly" ? dayOfWeek : undefined,
          day_of_month: frequency === "monthly" ? dayOfMonth : undefined,
        },
        {
          onSuccess: () => onOpenChange(false),
          onError: (err) => setError(err.message || "Failed to create schedule"),
        },
      );
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Report Schedule" : "Create Report Schedule"}
          </DialogTitle>
          <DialogDescription>
            Configure automated recurring report delivery via email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!isEditing && (
            <div>
              <Label htmlFor="report-type">Report Type</Label>
              <select
                id="report-type"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="posture">Posture Report</option>
                {isMsp && (
                  <option value="msp_portfolio">MSP Portfolio Report</option>
                )}
              </select>
            </div>
          )}

          <div>
            <Label htmlFor="frequency">Frequency</Label>
            <select
              id="frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          {frequency === "weekly" && (
            <div>
              <Label htmlFor="day-of-week">Day of Week</Label>
              <select
                id="day-of-week"
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(Number(e.target.value))}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {DAYS_OF_WEEK.map((day, i) => (
                  <option key={day} value={i}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
          )}

          {frequency === "monthly" && (
            <div>
              <Label htmlFor="day-of-month">Day of Month</Label>
              <select
                id="day-of-month"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label htmlFor="recipients">Recipients (comma-separated)</Label>
            <input
              id="recipients"
              type="text"
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              placeholder="admin@corp.com, soc@corp.com"
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Maximum 10 recipients. Reports will be sent as PDF attachments.
            </p>
          </div>

          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Update Schedule" : "Create Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
